import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { RiHistoryFill } from 'react-icons/ri';

import { BiDownload } from 'react-icons/bi';
import api from '../../services/api';
import { useAlert } from '../../hooks/alert';
import { useAuth } from '../../hooks/auth';
import getParamsUri from '../../utils/getParamsUri';

import { Client } from '../Clients';

import Input from '../../components/Input';
import Select from '../../components/Select';
import Textarea from '../../components/Textarea';
import { Budget } from '../Budgets';
import getFormatedDateTime from '../../utils/getFormatedDateTime';
import getFormatedMoney from '../../utils/getFormatedMoney';

interface BudgetsRegisterParams {
  client_id: string;
}

interface BudgetForm {
  client_id?: string;
  files?: string;
  obs?: string;
  type?: string;
}

interface BudgetParams {
  id: string;
  action: 'accept' | 'reject';
}

const BudgetsRegister: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { action, id } = useParams<BudgetParams>();

  const { showAlert } = useAlert();
  const { user } = useAuth();

  const history = useHistory();
  const location = useLocation();

  const [budget, setBudget] = useState<Budget>();
  const [files, setFiles] = useState<FileList | null>(null);
  const [clients, setClients] = useState<Client[]>();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState(false);
  const [type, setType] = useState('');

  // Details
  useEffect(() => {
    setLoading(true);

    if (!id) {
      setLoading(false);
      return;
    }

    try {
      api
        .get('budgets-details', { params: { id } })
        .then(response => {
          setBudget(response.data);
        })
        .then(() => {
          setLoading(false);
        });
    } catch (error) {
      showAlert({
        type: 'error',
        title: 'Algum erro aconteceu',
        description:
          'Não foi possível buscar os detalhes desse orçamento. Tente recarregar a página.',
      });
      setLoading(false);
    }
  }, [id, showAlert, location.pathname]);

  // Action accept/reject
  useEffect(() => {
    if (action === 'accept' && id) {
      setLoading(true);

      api
        .post('budgets-action', { action, id })
        .then(response => {
          showAlert({
            type: 'success',
            title: 'Ação executada com sucesso',
            description: response.data?.success,
          });

          history.push(`/budgets/${id}`);
        })
        .catch(error => {
          showAlert({
            type: 'error',
            title: 'Algum erro aconteceu',
            description: error.response.data?.error,
          });
        });
    } else if (action === 'reject') {
      showAlert({
        type: 'info',
        title: 'Informe os campos abaixo',
        description:
          'Escreva suas observações no final da página e clique em Enviar',
      });
    }
  }, [action, history, id, showAlert]);

  // Client list
  useEffect(() => {
    if (id) return;

    try {
      setLoading(true);
      api.get<Client[]>('user-clients-list').then(async response => {
        setClients(response.data);
        const params = getParamsUri<BudgetsRegisterParams>(location.search);
        if (params) {
          formRef.current?.setFieldValue('client_id', params.client_id);
        }
      });
    } catch (error) {
      showAlert({
        type: 'error',
        title: 'Algum erro aconteceu',
        description: error.response?.data?.error,
      });
    } finally {
      setLoading(false);
    }
  }, [budget, id, location.search, showAlert]);

  // Handle submit form
  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);

      const formCurrent = formRef.current;
      const formData = formCurrent?.getData() as BudgetForm;

      let errorMessage = `Informe o Cliente e selecione Arquivos para enviar.`;
      const mustSelectClient = !!formCurrent?.getFieldRef('client_id');
      if (!mustSelectClient) {
        errorMessage = `Selecione Arquivos para enviar.`;
      }

      if (formData) {
        if (mustSelectClient && !formData.client_id) {
          showAlert({
            type: 'error',
            title: 'Verifique os dados informados',
            description: errorMessage,
          });
          return;
        }

        if (!id && !files) {
          showAlert({
            type: 'error',
            title: 'Verifique os dados informados',
            description: 'Informe um ou mais arquivos para envio',
          });
          return;
        }

        if (!id && !type) {
          showAlert({
            type: 'error',
            title: 'Verifique os dados informados',
            description: 'Informe o tipo do material',
          });
          return;
        }

        const data = new FormData();

        if (budget) {
          data.append('id', String(budget.id));
        }

        if (files) {
          Array.from(files).forEach(file => {
            data.append('files[]', file);
          });
        }

        if (formData.client_id) {
          data.append('client_id', formData.client_id);
        }

        if (formData.type) {
          data.append('type', formData.type);
        }

        if (formData.obs) {
          data.append('obs', formData.obs);
        }

        await api.post('budgets', data);

        showAlert({
          type: 'success',
          title: 'Solicitação encaminhada com sucesso!',
          description: 'Aguarde nosso retorno através de seu e-mail.',
        });

        history.push('/budgets');
      }
    } catch (error) {
      showAlert({
        type: 'error',
        title: 'Algum erro aconteceu',
        description: 'Selecione os arquivos e tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  }, [budget, files, history, id, showAlert, type]);

  // Handle input file
  const handleFilesChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(e.target.files);
    }
  }, []);

  // Handle input type
  const handleTypeChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    if (e.type) {
      setType(e.type);
    }
  }, []);

  // Handle logs show
  const handleLogs = useCallback(() => {
    setLogs(!logs);
  }, [logs]);

  return (
    <>
      <Form onSubmit={handleSubmit} ref={formRef}>
        <h5 className="spacer">
          {id ? `ORÇAMENTO Nº ${id}` : `NOVO ORÇAMENTO`}
        </h5>

        {!id && (
          <p>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Maecenas
            molestie elit vitae varius maximus.
          </p>
        )}

        <div className="spacer" />

        {id && (
          <>
            <Input name="id" type="hidden" value={budget?.id} />
            <div className="row">
              <div className="col">
                <div className="form-group">
                  <label htmlFor="company">EMPRESA</label>
                  <Input
                    name="company"
                    type="text"
                    className="form-control"
                    id="company"
                    value={budget?.company}
                    disabled
                  />
                </div>
              </div>
              <div className="col">
                <div className="form-group">
                  <label htmlFor="cnpj">CNPJ</label>
                  <Input
                    name="cnpj"
                    type="text"
                    className="form-control"
                    id="cnpj"
                    value={budget?.cnpj}
                    disabled
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col">
                <div className="form-group">
                  <label htmlFor="price">ORÇAMENTO</label>
                  <Input
                    name="price"
                    type="text"
                    className="form-control"
                    id="price"
                    value={(budget && getFormatedMoney(budget.price)) || ''}
                    disabled
                  />
                </div>
              </div>
              <div className="col">
                <div className="form-group">
                  <label htmlFor="status">STATUS</label>
                  <Input
                    name="status"
                    type="text"
                    className="form-control"
                    id="status"
                    value={budget?.status}
                    disabled
                  />
                </div>
              </div>
            </div>

            {budget?.logs && budget.logs.length ? (
              <>
                <div className="text-right">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={handleLogs}
                  >
                    VER HISTÓRICO
                    {` `}
                    <RiHistoryFill size={18} />
                  </button>
                </div>
                {logs && (
                  <>
                    <h5>HISTÓRICO</h5>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>DATA/HORA</th>
                          <th>OBSERVAÇÕES</th>
                          <th>SITUAÇÃO</th>
                          <th>RESPONSÁVEL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {budget.logs.map(log => (
                          <tr key={log.id}>
                            <td>{getFormatedDateTime(log.datetime)}</td>
                            <td>{log.observacoes}</td>
                            <td>{log.status}</td>
                            <td>
                              {!log.usuario_responsavel ||
                              log.usuario_responsavel === '0'
                                ? 'Eu'
                                : 'JC'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="spacer" />
                  </>
                )}
              </>
            ) : (
              <></>
            )}

            {budget?.files && budget.files.length ? (
              <>
                <h5>ARQUIVO(S)</h5>
                <table className="table">
                  <tbody>
                    {budget.files.map(file => (
                      <tr key={file.id}>
                        <td>
                          <a
                            href={`${file.path}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {file.path}
                          </a>
                        </td>
                        <td className="text-right">
                          {file.final && (
                            <div className="badge badge-success">
                              Arquivo final
                            </div>
                          )}
                        </td>
                        <td className="text-right icons">
                          <a
                            href={`${file.path}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <BiDownload size={30} />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <></>
            )}
          </>
        )}

        {user.agency && !id && (
          <div className="row">
            <div className="col">
              <div className="form-group">
                <label htmlFor="client_id">CLIENTE</label>
                <Select
                  className="form-control"
                  name="client_id"
                  id="client_id"
                >
                  <option value="">
                    Selecione um cliente ou cadastre um novo
                  </option>
                  {clients &&
                    clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {`${client.name} - CNPJ: ${client.cnpj}`}
                      </option>
                    ))}
                </Select>
              </div>
            </div>
            <div className="col-auto">
              <Link
                to="/clients/register"
                className="btn btn-primary btn-sm mt-4"
              >
                Cadastrar novo cliente
              </Link>
            </div>
          </div>
        )}

        {!id || (budget && budget.status_id < 2) ? (
          <>
            {id && <h5 className="mt-4">SOLICITAR REVISÃO / EDITAR</h5>}
            <div className="form-group">
              <label htmlFor="file">SELECIONE UM OU MAIS ARQUIVOS ABAIXO</label>
              <Input
                name="file"
                type="file"
                className="form-control input"
                id="file"
                multiple
                onChange={handleFilesChange}
              />
              <div className="help-block" />
            </div>

            {!id && (
              <div className="form-group">
                <label htmlFor="client_id">TIPO DO MATERIAL</label>
                <Select
                  className="form-control"
                  name="type"
                  id="type"
                  onChange={handleTypeChange}
                >
                  <option value="">Selecione um tipo</option>
                  <option value="Edital">Edital</option>
                  <option value="Ata">Ata</option>
                  <option value="Balanço">Balanço</option>
                  <option value="Outro">Outro</option>
                </Select>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="obs">OBSERVAÇÕES</label>
              <Textarea
                name="obs"
                id="obs"
                cols={30}
                rows={5}
                className="form-control"
                placeholder={`Utilize este campo informar, entre outros detalhes, o seguinte:\nData(s) para veiculação: \nCaderno para veiculação: `}
              />
            </div>
          </>
        ) : (
          <></>
        )}

        <div className="row">
          <div className="col d-flex align-items-center">
            <Link to="/budgets">Voltar</Link>
          </div>
          <div className="col text-right">
            {!id || (budget && budget.status_id < 2) ? (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'CARREGANDO...' : 'ENVIAR'}
              </button>
            ) : (
              <></>
            )}
          </div>
        </div>
      </Form>
    </>
  );
};

export default BudgetsRegister;
